import {
  ADD_MODELS_TO_ARCHIVED_SUCCESS,
  ADD_MODEL_TO_ARCHIVED_SUCCESS,
  ADD_MODEL_TO_FAVORITES_SUCCESS,
  FAILED,
  MODELS_ARCHIVED_FAILED,
  REMOVED_MODELS_FROM_ARCHIVED_SUCCESS,
  REMOVED_MODEL_FROM_ARCHIVED_SUCCESS,
  REMOVED_MODEL_FROM_FAVORITES_SUCCESS,
  SUCCESS,
} from '@App/app/configs/toastr-events.config';
import {
  SUCCESS_TOASTR_CONFIG,
  WARNING_TOASTR_CONFIG,
} from '@App/app/configs/toastr-messages.config';
import { AuthService } from '@App/app/auth/auth.service';
import { Model } from '@App/app/entities/models/model.model';
import { ModelsHttpService } from '@App/app/pages/models/services/models-http-service/models-http.service';
import { ModelsSitesService } from '@App/app/pages/sites/shared/site-services/models-sites.service';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NbToastrService } from '@nebular/theme';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, combineLatest } from 'rxjs';
import { catchError, finalize, map, mergeMap, tap } from 'rxjs/operators';
import { changeModels, favoriteModel, loadAllModels, updateModel } from '../actions/models.actions';

@Injectable()
export class ChangeModelEffects {
  constructor(
    private actions: Actions,
    private modelsHttpService: ModelsHttpService,
    private toastrService: NbToastrService,
    private sitesService: ModelsSitesService,
    private authService: AuthService,
    private router: Router,
  ) {}

  changeModels = createEffect(() => {
    return this.actions.pipe(
      ofType(changeModels),
      mergeMap(({ models }) => {
        const archiveAction = !models[0].isArchived;
        this.sitesService.setSelectedEntities([]);
        const newModels = models.map((model: Model) => {
          return { ...model, isArchived: !model.isArchived };
        });
        return combineLatest(
          newModels.map((newModel) => {
            return this.modelsHttpService.putModel(Number(newModel.id), newModel);
          }),
        )
          .pipe(
            map((resp: Model[]) => {
              const archiveSuccess = resp.every(
                (res, index) => res.isArchived !== models[index].isArchived,
              );
              if (archiveSuccess) {
                this.toastrService.show(
                  archiveAction
                    ? ADD_MODELS_TO_ARCHIVED_SUCCESS
                    : REMOVED_MODELS_FROM_ARCHIVED_SUCCESS,
                  SUCCESS,
                  {
                    ...SUCCESS_TOASTR_CONFIG,
                  },
                );
              } else {
                this.toastrService.show(MODELS_ARCHIVED_FAILED, FAILED, {
                  ...WARNING_TOASTR_CONFIG,
                });
              }
              return loadAllModels();
            }),
            catchError(() => EMPTY),
          )
          .pipe(
            finalize(() => {
              this.sitesService.hideSpinner();
            }),
          );
      }),
    );
  });

  changeModel = createEffect(() => {
    return this.actions.pipe(
      ofType(updateModel),
      mergeMap(({ model }) =>
        this.putModelAndToast(model, (resp) =>
          resp.isArchived ? ADD_MODEL_TO_ARCHIVED_SUCCESS : REMOVED_MODEL_FROM_ARCHIVED_SUCCESS,
        ).pipe(tap(() => this.navigateToModelsList())),
      ),
    );
  });

  favoriteModel = createEffect(() => {
    return this.actions.pipe(
      ofType(favoriteModel),
      mergeMap(({ model }) =>
        this.putModelAndToast(model, (resp) =>
          resp.isFavorite ? ADD_MODEL_TO_FAVORITES_SUCCESS : REMOVED_MODEL_FROM_FAVORITES_SUCCESS,
        ),
      ),
    );
  });

  /**
   * PUTs a single model (nulling out an incomplete location), shows a success toast whose
   * message is derived from the response, and dispatches a reload. Shared by the
   * archive (`changeModel`) and favorite (`favoriteModel`) effects.
   */
  private putModelAndToast(model: Model, getMessage: (resp: Model) => string) {
    const modelToUpdate = { ...model };
    if (!modelToUpdate.location?.latitude || !modelToUpdate.location?.longitude) {
      modelToUpdate.location = null;
    }
    return this.modelsHttpService.putModel(Number(model.id), modelToUpdate).pipe(
      map((resp: Model) => {
        this.toastrService.show(getMessage(resp), SUCCESS, {
          ...SUCCESS_TOASTR_CONFIG,
        });
        return loadAllModels();
      }),
      catchError(() => EMPTY),
    );
  }

  /**
   * Navigates back to the models list. Called only after a model update has been
   * persisted, so that leaving the details page (which calls `window.stop()` on
   * destroy) cannot abort the in-flight request.
   */
  private navigateToModelsList() {
    const endpoint = this.authService.getCurrentEndpoint();
    const sitesUrl = endpoint ? `${endpoint.id}/models` : '';
    this.router.navigate([sitesUrl]);
  }
}
